import { nonce } from '../utils/runtime';

export const LOOTLOG_API_URL = '/wp-json/pfandpirat/v1/lootlog';
export const LOOT_CLAIM_API_URL = '/wp-json/pfandpirat/v1/loot/claim';
export const LOOT_CLAIM_STATUS_API_URL = '/wp-json/pfandpirat/v1/loot/claim-status';
export const LOOT_CLAIM_TIMEOUT_MS = 8000;
export const LOOT_CLAIM_STATUS_TIMEOUT_MS = 4000;

export class LootApiError extends Error {
  constructor(message, { code = 'loot_request_failed', status = 0, data = null } = {}) {
    super(message || 'Loot-Anfrage fehlgeschlagen.');
    this.name = 'LootApiError';
    this.code = code;
    this.status = Number(status || 0);
    this.data = data;
  }
}

export function normalizeLootLogResponse(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  return [];
}

export function createLootRequestId() {
  if (globalThis?.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `loot-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function notifyAuthenticationRequired(error) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent('pfandpirat:authentication-required', {
    detail: { source: 'loot', code: error.code, status: error.status },
  }));
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new LootApiError('Der Loot-Server hat ungültige Daten geliefert.', {
      code: 'loot_invalid_response',
      status: response.status,
    });
  }
}

export async function requestLootJson(url, options = {}) {
  const {
    timeoutMs = 0,
    signal: externalSignal,
    ...fetchOptions
  } = options;
  const headers = {
    Accept: 'application/json',
    ...(fetchOptions.body ? { 'Content-Type': 'application/json' } : {}),
    ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
    ...(fetchOptions.headers || {}),
  };
  const timeoutController = timeoutMs > 0 && typeof AbortController !== 'undefined'
    ? new AbortController()
    : null;
  const forwardAbort = () => timeoutController?.abort(externalSignal?.reason);
  if (externalSignal && timeoutController) {
    if (externalSignal.aborted) forwardAbort();
    else externalSignal.addEventListener('abort', forwardAbort, { once: true });
  }
  const timeoutId = timeoutController
    ? setTimeout(() => timeoutController.abort('loot-request-timeout'), timeoutMs)
    : null;

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'same-origin',
      headers,
      signal: timeoutController?.signal || externalSignal,
    });
    const payload = await parseResponse(response);

    if (!response.ok) {
      const error = new LootApiError(payload?.message || 'Loot-Anfrage fehlgeschlagen.', {
        code: payload?.code || 'loot_request_failed',
        status: response.status,
        data: payload?.data || null,
      });

      if (response.status === 401 || response.status === 403 || error.code === 'rest_cookie_invalid_nonce') {
        notifyAuthenticationRequired(error);
      }

      throw error;
    }

    return payload;
  } catch (error) {
    const timedOut = timeoutController?.signal?.reason === 'loot-request-timeout';
    if (timedOut) {
      throw new LootApiError('Der Loot-Server antwortet gerade zu langsam.', {
        code: 'loot_request_timeout',
      });
    }
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    externalSignal?.removeEventListener?.('abort', forwardAbort);
  }
}

export async function loadLootLog() {
  const payload = await requestLootJson(LOOTLOG_API_URL);
  return normalizeLootLogResponse(payload);
}

export async function claimLocationLoot({ locationId, spotKey, latitude, longitude, accuracy, positionTimestamp, requestId }) {
  const body = {
    latitude: Number(latitude),
    longitude: Number(longitude),
    request_id: requestId || createLootRequestId(),
  };

  const numericLocationId = Number(locationId);
  if (Number.isInteger(numericLocationId) && numericLocationId > 0) {
    body.location_id = numericLocationId;
  }
  if (spotKey) {
    body.spot_key = String(spotKey);
  }

  if (Number.isFinite(Number(accuracy))) {
    body.accuracy = Number(accuracy);
  }
  if (Number.isFinite(Number(positionTimestamp))) {
    body.position_timestamp = Number(positionTimestamp);
  }

  return requestLootJson(LOOT_CLAIM_API_URL, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: LOOT_CLAIM_TIMEOUT_MS,
  });
}

export async function checkLootClaimStatus(requestId) {
  const params = new URLSearchParams({ request_id: String(requestId || '') });
  return requestLootJson(`${LOOT_CLAIM_STATUS_API_URL}?${params.toString()}`, {
    timeoutMs: LOOT_CLAIM_STATUS_TIMEOUT_MS,
  });
}

function wait(delayMs) {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(delayMs) || 0)));
}

export async function waitForLootClaimResolution(requestId, { attempts = 3, delayMs = 1500 } = {}) {
  const maxAttempts = Math.max(1, Math.min(5, Number(attempts) || 1));
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const result = await checkLootClaimStatus(requestId);
    if (!result?.pending && result?.item?.name) return result;
    if (attempt + 1 < maxAttempts) await wait(delayMs);
  }
  return null;
}

export function getLootErrorMessage(error) {
  if (error?.code === 'cooldown_active') {
    const retryAfter = Number(error?.data?.retry_after || 0);
    return retryAfter > 0
      ? `Cooldown aktiv! Noch ${Math.ceil(retryAfter / 60)} Min.`
      : 'Cooldown aktiv! Versuche es später noch einmal.';
  }

  if (error?.status === 401 || error?.code === 'loot_not_authenticated') {
    return 'Deine Anmeldung ist abgelaufen. Bitte melde dich erneut an.';
  }

  if (error?.status === 403 || error?.code === 'rest_cookie_invalid_nonce') {
    return 'Deine Sitzung ist abgelaufen. Bitte lade die Seite neu.';
  }

  if (error?.code === 'loot_location_inaccurate') {
    return 'Das GPS-Signal ist zu ungenau.';
  }

  if (error?.code === 'loot_location_stale') {
    return 'Der GPS-Standort ist zu alt. Bitte starte GPS erneut.';
  }

  if (error?.code === 'loot_request_timeout') {
    return 'Die Loot-Anfrage wurde nach 8 Sekunden beendet. Bitte warte kurz und prüfe den Lootlog, bevor du es erneut versuchst.';
  }

  if (error?.code === 'loot_claim_status_pending') {
    return 'Die Verbindung ist langsam. Ob deine Beute verbucht wurde, ist noch nicht eindeutig. Bitte prüfe gleich den Lootlog.';
  }

  if (error?.code === 'loot_too_far_away') {
    const distance = Number(error?.data?.distance_m || 0);
    return distance > 0 ? `Du bist noch ${Math.ceil(distance)} m entfernt.` : 'Du bist zu weit vom Loot-Ort entfernt.';
  }

  if (['loot_location_unavailable', 'loot_location_invalid', 'loot_location_not_found'].includes(error?.code)) {
    return 'Der Loot-Ort ist derzeit nicht verfügbar.';
  }

  return error?.message || 'Fehler beim Looten.';
}
